import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Checkbox, FormControlLabel, FormGroup, Tab, Tabs, Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import ApiClient from '../../loopback/apiClient';
import { AuswertungSetting } from '../../redux/types/auswertungSettingTypes';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import {
  fetchFullDigitalSurvey,
  resetDigitalSurvey,
  setSelectedDigitalsection,
  showErrorNotification,
  updateDigitalQuestion,
} from '../../redux/actions';
import useGetTrialById from '../../hooks/trial/useGetTrialById';
import { selectDigitalQuestionsBySection, selectDigitalSections, selectSelectedSection } from '../../redux/reducers/SurveyDataReducer';
import QuestionCollapsed from '../../components/survey/editor/QuestionCollapsed';
import AuswertungQuestionSettingDialog from '../../components/admin/auswertungSettings/AuswertungQuestionSettingDialog';
import { DigitalQuestion } from '../../redux/types';

type RouteProps = {
  trialId: string,
}

const AuswertungSettings = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { trialId } = useParams<RouteProps>();
  const trial = useGetTrialById(Number(trialId));
  const digitalSections = useAppSelector(selectDigitalSections);
  const selectedSection = useAppSelector(selectSelectedSection);
  const questionsPerSection = useAppSelector(selectDigitalQuestionsBySection);
  const [settings, setSettings] = useState<AuswertungSetting>();
  const [bufferQuestion, setBufferQuestion] = useState<DigitalQuestion>();

  useEffect(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((digitalSurveys) => digitalSurveys.filter((ds) => (!ds.exercise)))
      .then(([survey]) => dispatch(fetchFullDigitalSurvey(survey.id)));
    ApiClient.getAuswertungSetting(Number(trialId)).then((s) => setSettings(s));
    return () => {
      dispatch(resetDigitalSurvey);
    };
  }, [trialId, dispatch]);

  useEffect(() => {
    dispatch(setSelectedDigitalsection(digitalSections?.[0]?.id));
  }, [dispatch, digitalSections]);

  const settingsChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, c: boolean) => {
    if (!trial) {
      return;
    }
    const { ariaLabel } = event.target as { ariaLabel: keyof Pick<AuswertungSetting, 'hideSuccessRate'|'hideFailRate'|'hideOverallScore'|'hideSectionSummary'> };
    if (settings) {
      const updateSettingObj: AuswertungSetting = {
        ...settings,
        [ariaLabel]: c,
      };
      ApiClient.updateAuswertungSetting(updateSettingObj)
        .then((s) => setSettings(s))
        .catch((e) => dispatch(showErrorNotification('Unable to save changes, please try again')));
    } else {
      const newSettingObj = {
        trialId: trial.id,
        [ariaLabel]: c,
      };
      ApiClient.updateAuswertungSetting(newSettingObj)
        .then((s) => setSettings(s))
        .catch((e) => dispatch(showErrorNotification('Unable to save changes, please try again')));
    }
  };

  const onSaveQuestionSetting = useCallback((q: DigitalQuestion) => {
    dispatch(updateDigitalQuestion(q));
    setBufferQuestion(undefined);
  }, [dispatch]);

  return (
    <Box>
      <Box sx={{ m: 2 }}>
        <Typography variant="h6">Auswertung Settings</Typography>
        <Typography variant="h4">
          {trial?.name}
        </Typography>
      </Box>
      <Box sx={{ m: 4 }}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5">Hide details</Typography>
          <FormGroup>
            <FormControlLabel control={<Checkbox onChange={settingsChangeHandler} checked={settings?.hideSuccessRate || false} inputProps={{ 'aria-label': 'hideSuccessRate' }} />} label={<FormattedMessage id="enrollmentsmanager.quotaPassedSubmissions" />} />
            <FormControlLabel
              control={<Checkbox onChange={settingsChangeHandler} checked={settings?.hideFailRate || false} inputProps={{ 'aria-label': 'hideFailRate' }} />}
              label={(
                <>
                  <FormattedMessage id="trialdetails.failRate" />
                  {' '}
                  (survey v2)
                </>
  )}
            />
            <FormControlLabel control={<Checkbox onChange={settingsChangeHandler} checked={settings?.hideOverallScore || false} inputProps={{ 'aria-label': 'hideOverallScore' }} />} label={<FormattedMessage id="enrollmentsmanager.totalScore" />} />
            <FormControlLabel control={<Checkbox onChange={settingsChangeHandler} checked={settings?.hideParticipantCount || false} inputProps={{ 'aria-label': 'hideParticipantCount' }} />} label={<FormattedMessage id="enrollmentsmanager.passedSubmissions" />} />
            <FormControlLabel control={<Checkbox onChange={settingsChangeHandler} checked={settings?.hideSectionSummary || false} inputProps={{ 'aria-label': 'hideSectionSummary' }} />} label={<FormattedMessage id="enrollmentsmanager.passedSubmissionsSection" />} />
          </FormGroup>
        </Box>
      </Box>
      <Box sx={{ m: 4 }}>
        <Typography variant="h5">
          <FormattedMessage id="questionEditor.hideAggregation" />
        </Typography>
        <Tabs
          value={selectedSection?.id || false}
          onChange={(e, v) => dispatch(setSelectedDigitalsection(v))}
          aria-label="Sections"
        >
          {
            digitalSections.map((s) => (
              <Tab
                key={s.id}
                value={s.id}
                label={s.name}
                wrapped
              />
            ))
          }
        </Tabs>
        <Box>
          <QuestionCollapsed
            questions={questionsPerSection}
            setSelectedQuestion={setBufferQuestion}
            selectedQuestion={bufferQuestion}
            dialog={(
              <AuswertungQuestionSettingDialog
                question={bufferQuestion}
                onSaveQuestionSetting={onSaveQuestionSetting}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AuswertungSettings;
